import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { makeStyles, shorthands, tokens, Tooltip } from "@fluentui/react-components";
import { ChevronRight16Regular } from "@fluentui/react-icons";
import {
  abandonCartDialogContent,
  abandonCartDialogTitle,
  appInsightsEvents,
  HelperUtils,
  MAX_BREADCRUMB_TEXT_LENGTH,
  trackEvent,
  useAppStore,
} from "../../../common";
import { ConfirmationDialog, ConfirmationDialogProps } from "../ConfirmationDialog";

interface BreadcrumbItem {
  text: string;
  path: string;
  isActive: boolean;
}

const getDisplayText = (text: string): { displayText: string; isTruncated: boolean } => {
  if (text.length <= MAX_BREADCRUMB_TEXT_LENGTH) {
    return { displayText: text, isTruncated: false };
  }

  const truncatedText = `${text
    .slice(0, Math.max(MAX_BREADCRUMB_TEXT_LENGTH - 3, 0))
    .trimEnd()}...`;
  return { displayText: truncatedText, isTruncated: true };
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    overflowX: "auto",
    whiteSpace: "nowrap",
    marginBottom: "0", // Reduce gap between breadcrumb and content
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE/Edge
    "::-webkit-scrollbar": {
      display: "none", // Safari and Chrome
    },
    "@media (min-width: 321px) and (max-width: 499px)": {
      flexWrap: "wrap",
      whiteSpace: "normal",
      overflowX: "visible",
      rowGap: "4px",
      columnGap: "4px",
    },
    "@media (max-width: 320px)": {
      flexWrap: "nowrap",
      whiteSpace: "nowrap",
      overflowX: "hidden",
    },
  },
  containerCafePage: {
    display: "flex",
    alignItems: "center",
    overflowX: "auto",
    whiteSpace: "nowrap",
    marginBottom: "0", // Reduce gap between breadcrumb and content
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE/Edge
    "::-webkit-scrollbar": {
      display: "none", // Safari and Chrome
    },
    "@media (min-width: 321px) and (max-width: 499px)": {
      flexWrap: "wrap",
      whiteSpace: "normal",
      overflowX: "visible",
      rowGap: "4px",
      columnGap: "4px",
    },
    // NOWRAP Added for 400% Zoom
    "@media (max-width: 320px)": {
      flexWrap: "nowrap",
      whiteSpace: "nowrap",
      overflowX: "hidden",
    },
  },
  breadcrumbItem: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.textDecoration("none"),
    marginRight: "4px",
    maxWidth: "100%",
    "@media (min-width: 321px) and (max-width: 499px)": {
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
    // NOWRAP Added for 400% Zoom
    "@media (max-width: 320px)": {
      maxWidth: "45vw",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "inline-block",
      fontSize: tokens.fontSizeBase100,
    },
  },
  breadcrumbSeparator: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginLeft: "4px",
    marginRight: "4px",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  activeItem: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    "@media (min-width: 321px) and (max-width: 499px)": {
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
    // NOWRAP Added for 400% Zoom
    "@media (max-width: 320px)": {
      maxWidth: "45vw",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "inline-block",
      fontSize: tokens.fontSizeBase100,
    },
  },
});

// Import and use breadcrumb utils for data management

export const Breadcrumb: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const isCafePage = location.pathname.match(/^\/cafe\/[^/]+$/); // Check if we're on a base cafe page
  const params = useParams<{
    cafeId?: string;
    stationId?: string;
    menuItemId?: string;
    itemId?: string;
    orderId?: string;
    editItemId?: string;
  }>();
  const navigate = useNavigate();
  const [dialogProps, setDialogProps] = React.useState<ConfirmationDialogProps>();

  // Get necessary data from store
  const buildings = useAppStore((state) => state.buildings.values);
  const orders = useAppStore((state) => state.orders.values);
  const ordersHistory = useAppStore((state) => state.ordersHistory?.values);
  const client = useAppStore((state) => state.client);
  const selectedDate = useAppStore((state) => state.selectedDate);
  const [appInsights, context, cartState] = useAppStore((state) => [
    state.appInsights,
    state.context,
    state.cart,
  ]);
  const { value: currentCart, abandonCart } = cartState;
  const hasCartItems = Boolean(currentCart?.items?.length);

  // States for various names needed in breadcrumbs
  const [cafeName, setCafeName] = React.useState<string>("Cafe");
  const [stationName, setStationName] = React.useState<string>("Station");

  React.useEffect(() => {
    setCafeName("Cafe");
  }, [params.cafeId]);

  React.useEffect(() => {
    setStationName("Station");
  }, [params.stationId]);

  React.useEffect(() => {
    const cafeId = params.cafeId;
    if (!cafeId) return;

    // Still try to get the name from buildings data
    const allCafes = buildings?.flatMap((building) => building.cafeList) || [];
    const foundCafe = allCafes.find((c) => c.id === cafeId);
    if (foundCafe?.name) {
      setCafeName(foundCafe.name);
      return;
    }

    // If we don't have the name yet and have client, try to fetch it
    if (!foundCafe?.name && client && cafeId) {
      let isMounted = true;
      const fetchCafeData = async () => {
        try {
          const cafeResponse = await client.getCafe(cafeId);
          if (isMounted && cafeResponse.name) {
            setCafeName(cafeResponse.name);
          }
        } catch (error) {
          if (isMounted) {
            console.error("Error fetching cafe name:", error);
          }
        }
      };

      fetchCafeData();

      return () => {
        isMounted = false;
      };
    }
  }, [buildings, params.cafeId, client]);

  const cafe = React.useMemo(() => {
    const cafeId = params.cafeId;
    if (!cafeId) return null;

    // First check from buildings data
    const allCafes = buildings?.flatMap((building) => building.cafeList) || [];
    const foundCafe = allCafes.find((c) => c.id === cafeId);
    if (foundCafe) return foundCafe;

    // If not found, use the cafeName we've set
    return { id: cafeId, name: cafeName };
  }, [buildings, params.cafeId, cafeName]);

  // Fetch station name when needed
  React.useEffect(() => {
    const cafeId = params.cafeId;
    const stationId = params.stationId;

    if (cafeId && stationId && client) {
      // Try to get station information from the API
      const fetchStationName = async () => {
        try {
          const stationsResponse = await client.getCafeStationsList(
            cafeId,
            selectedDate?.get("day"),
          );
          const stationData = stationsResponse.items?.find((s) => s.id === stationId);
          if (stationData && stationData.name) {
            setStationName(stationData.name);
          }
        } catch (error) {
          console.error("Error fetching station name:", error);
        }
      };

      fetchStationName();
    }
  }, [params.cafeId, params.stationId, client, selectedDate]);

  const station = React.useMemo(() => {
    const stationId = params.stationId;
    if (!stationId) return null;

    return { id: stationId, name: stationName }; // Use the fetched or default name
  }, [params.stationId, stationName]);

  // Use state for menu item name since we might need to fetch it asynchronously
  const [menuItemName, setMenuItemName] = React.useState<string>("Menu Item");

  React.useEffect(() => {
    setMenuItemName("Menu Item");
  }, [params.menuItemId]);

  // Fetch menu item name when needed
  React.useEffect(() => {
    const cafeId = params.cafeId;
    const stationId = params.stationId;
    const menuItemId = params.menuItemId;

    if (cafeId && stationId && menuItemId && client && selectedDate) {
      // Try to get menu item information from the API
      const fetchMenuItemName = async () => {
        try {
          const menus = await client.getStationFullMenusForDayOfWeek(
            cafeId,
            stationId,
            selectedDate.get("day"),
          );

          // Search for the menu item in all menus
          let itemName = "Menu Item"; // Default name
          menus.items?.forEach((menu) => {
            menu.items?.forEach((item) => {
              if (item.id === menuItemId && item.name) {
                itemName = item.name;
              }
            });
          });

          // Update the menu item name
          setMenuItemName(itemName);
        } catch (error) {
          console.error("Error fetching menu item name:", error);
        }
      };

      fetchMenuItemName();
    }
  }, [params.cafeId, params.stationId, params.menuItemId, client, selectedDate]);

  const menuItem = React.useMemo(() => {
    const menuItemId = params.menuItemId;
    if (!menuItemId) return null;

    return { id: menuItemId, name: menuItemName }; // Use the fetched or default name
  }, [params.menuItemId, menuItemName]);

  const order = React.useMemo(() => {
    const { orderId } = params;
    if (!orderId) return null;

    const allOrders = orders || [];
    const pastOrders = ordersHistory || [];
    return [...allOrders, ...pastOrders].find((o) => o.id === orderId);
  }, [params.orderId, orders, ordersHistory]);

  const handleHomeClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, targetPath: string) => {
      if (!currentCart || !hasCartItems || !abandonCart) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const { cafeId, id: cartId, cafeName } = currentCart;

      setDialogProps({
        content: abandonCartDialogContent,
        open: true,
        title: abandonCartDialogTitle,
        confirmAction: async () => {
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.AbandonCartDialog.HomeBreadcrumbConfirmed,
            context,
            {
              cafeId,
              cartId,
              cafeName,
              component: "Breadcrumb",
            },
          );

          try {
            await abandonCart();
            trackEvent(appInsights, appInsightsEvents.Usage.Actions.CafeChanged, context, {
              cafeId,
              cartId,
              cafeName,
              component: "Breadcrumb",
            });
            navigate(targetPath);
          } finally {
            setDialogProps(undefined);
          }
        },
        dismissAction: () => {
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.AbandonCartDialog.HomeBreadcrumbDismissed,
            context,
            {
              cafeId,
              cartId,
              cafeName,
              component: "Breadcrumb",
            },
          );
          setDialogProps(undefined);
        },
      });
    },
    [abandonCart, appInsights, context, currentCart, hasCartItems, navigate],
  );

  // Generate breadcrumb items based on current route
  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [
      { text: "Home", path: "/", isActive: location.pathname === "/" },
    ];

    // Cafe page
    if (params.cafeId) {
      items.push({
        text: cafe?.name || cafeName || "Cafe",
        path: `/cafe/${params.cafeId}`,
        isActive: location.pathname === `/cafe/${params.cafeId}`,
      });
    }

    // Station menu page
    if (params.cafeId && params.stationId) {
      items.push({
        text: station?.name || stationName || "Station",
        path: `/cafe/${params.cafeId}/station/${params.stationId}`,
        isActive: location.pathname === `/cafe/${params.cafeId}/station/${params.stationId}`,
      });
    }

    // Menu item details page
    if (params.cafeId && params.stationId && params.menuItemId) {
      items.push({
        text: menuItem?.name || menuItemName || "Menu Item",
        path: `/cafe/${params.cafeId}/station/${params.stationId}/item/${params.menuItemId}`,
        isActive:
          !location.pathname.includes("/edit") &&
          location.pathname ===
            `/cafe/${params.cafeId}/station/${params.stationId}/item/${params.menuItemId}`,
      });
    }

    // Edit item page
    if (params.editItemId && location.pathname.includes("/edit")) {
      items.push({
        text: "Edit",
        path: location.pathname,
        isActive: true,
      });
    }

    // Cart page
    if (location.pathname.includes("/checkout")) {
      // For checkout page, include station name in the breadcrumb if available
      if (currentCart && params.cafeId) {
        const stationNamesStr =
          HelperUtils.getInstance().getUniqueFormattedStationNames(currentCart);
        if (stationNamesStr) {
          // Get first station ID for link (if there's only one station)
          const firstStationId =
            currentCart.items && currentCart.items.length > 0
              ? currentCart.items[0].stationId
              : undefined;

          // Add station name to breadcrumb before cart
          items.push({
            text: stationNamesStr,
            // If only one station, link to that station's page, otherwise no direct link
            path:
              firstStationId && currentCart.items.every((item) => item.stationId === firstStationId)
                ? `/cafe/${params.cafeId}/station/${firstStationId}`
                : "#",
            isActive: false,
          });
        }
      }

      items.push({
        text: "Cart",
        path: location.pathname,
        isActive: true,
      });
    }

    // Order history page
    if (location.pathname === "/orders/past") {
      items.push({
        text: "Order History",
        path: "/orders/past",
        isActive: true,
      });
    }

    // Order details page
    if (location.pathname.includes("/orders/past/") && params.orderId) {
      items.push({
        text: "Order History",
        path: "/orders/past",
        isActive: false,
      });

      items.push({
        text: order ? `Order #${order.orderNumber || params.orderId}` : `Order #${params.orderId}`,
        path: location.pathname,
        isActive: true,
      });
    }

    // Cafe-specific order details
    if (
      params.cafeId &&
      params.orderId &&
      location.pathname.includes(`/cafe/${params.cafeId}/order/`)
    ) {
      items.push({
        text: "Order Details",
        path: location.pathname,
        isActive: true,
      });
    }

    return items;
  };

  const breadcrumbItems = getBreadcrumbItems();

  // Only render if we have more than just the home item
  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <>
      {dialogProps && <ConfirmationDialog {...dialogProps} />}
      <div
        className={isCafePage ? styles.containerCafePage : styles.container}
        data-testid="breadcrumb"
      >
        {breadcrumbItems.map((item, index) => {
          const { displayText, isTruncated } = getDisplayText(item.text);

          const linkElement = (
            <Link
              to={item.path}
              className={`${styles.breadcrumbItem} ${item.isActive ? styles.activeItem : ""}`}
              title={item.text}
              aria-current={item.isActive ? "page" : undefined}
              aria-label={isTruncated ? item.text : undefined}
              onClick={index === 0 ? (event) => handleHomeClick(event, item.path) : undefined}
            >
              {displayText}
            </Link>
          );

          return (
            <React.Fragment key={`${item.path}-${index}`}>
              {index > 0 && (
                <span className={styles.breadcrumbSeparator} aria-hidden="true">
                  <ChevronRight16Regular />
                </span>
              )}
              {isTruncated ? (
                <Tooltip content={item.text} relationship="label">
                  {linkElement}
                </Tooltip>
              ) : (
                linkElement
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};
